<script setup lang="ts">
	import { useDebounceFn } from '@vueuse/core'

	useHead({
		title: 'MyCovergenius | Terms and Conditions',
		meta: [
			{
				name: 'description',
				content:
					'Review the terms and conditions for using MyCovergenius, a platform that provides insurance services and products. Ensure you understand the legal agreements and policies before accessing our services.'
			}
		]
	})
	useSeoMeta({
		title: 'Terms and Conditions | MyCoverGenius',
		description:
			'Review the terms and conditions for using MyCovergenius, a platform that provides insurance services and products. Ensure you understand the legal agreements and policies before accessing our services.',
		ogTitle: 'Terms and Conditions | MyCoverGenius',
		ogDescription:
			'Review the terms and conditions for using MyCovergenius, a platform that provides insurance services and products. Ensure you understand the legal agreements and policies before accessing our services.',
		ogType: 'website',
		articleTag: ['Terms and Conditions', 'MyCoverGenius'],
		ogUrl: `${import.meta.env.VITE_BUSINESS_URL}/terms-and-conditions`,
		twitterTitle: 'Terms and Conditions | MyCoverGenius',
		twitterDescription:
			'Review the terms and conditions for using MyCovergenius, a platform that provides insurance services and products. Ensure you understand the legal agreements and policies before accessing our services.',
		twitterSite: '@mycovergenius',
		twitterAppUrlGoogleplay: `${import.meta.env.VITE_BUSINESS_URL}/terms-and-conditions`,
		twitterAppUrlIphone: `${import.meta.env.VITE_BUSINESS_URL}/terms-and-conditions`,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
</script>

<template>
	<div class="wrapper">
		<ui-pad :ui="{ margin: 'md:mt-10', paddingInline: 'md:px-10 px-3' }">
			<article>
				<h1 class="c20" id="h.pwh3xbd42eo8">
					<span class="c13 c18">MyCoverGenius - Terms and Conditions</span>
				</h1>
				<p class="c9">
					<span class="c0"
						>These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of the services
						provided by MyCoverGenius Limited through its mobile and web platforms. By accessing or using
						our services, you agree to comply with and be legally bound by these Terms. Please read them
						carefully before using our platform.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.vjl7a1afg11n">
					<span class="c3">1. Definitions</span>
				</h2>
				<ul class="c1 lst-kix_rsxb1kdaih5c-0 start">
					<li class="c5 li-bullet-0">
						<span class="c0"
							>Policy: Any insurance coverage or contract issued through MyCoverGenius.<br
						/></span>
					</li>
					<li class="c5 li-bullet-0">
						<span class="c0">Premium: The amount you pay to maintain coverage under a Policy.<br /></span>
					</li>
					<li class="c5 li-bullet-0">
						<span class="c0"
							>Claim: A request you make for payment under a Policy because a covered event occurred.<br
						/></span>
					</li>
					<li class="c5 li-bullet-0">
						<span class="c0"
							>Insurer: The insurance company underwriting the Policy (may be a third party).<br
						/></span>
					</li>
					<li class="c5 li-bullet-0">
						<span class="c0"
							>Regulator: The National Insurance Commission (NAICOM) or equivalent regulatory body in
							Nigeria.<br
						/></span>
					</li>
					<li class="c5 li-bullet-0">
						<span class="c0"
							>Subrogation: The right of the insurer to pursue recovery from a third party responsible for
							your loss after paying your claim.<br
						/></span>
					</li>
				</ul>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.8j7x6rbepjrm">
					<span class="c3">2. Eligibility</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>You must be at least 18 years old and legally capable of entering into binding contracts.<br />
						You must provide accurate, current, and complete information as required by MyCoverGenius or the
						Insurer.<br />
						MyCoverGenius reserves the right to reject or suspend your application or coverage based on
						eligibility criteria, underwriting rules, or regulatory requirements.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.yk9mkoas0ggk">
					<span class="c3">3. Premiums and Payments</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>MyCoverGenius offers different cover periods depending on the product, including monthly,
						quarterly, biannual, and annual options. Some products are only available on an annual basis.
						You are required to select the cover period that best suits you at the point of purchase or
						renewal.</span
					>
				</p>
				<h3 class="c4" id="h.ejr7be7bwapg">
					<span class="c15 c13">Payment and Renewal</span>
				</h3>
				<p class="c9">
					<span class="c0"
						>Premium payments or renewals must be made no later than 7 days before the end of your current
						cover period, or on the exact day of expiration, to avoid lapses in coverage.</span
					>
				</p>
				<h3 class="c4" id="h.83vwxn24p4hy">
					<span class="c15 c13">Payment Methods</span>
				</h3>
				<p class="c9">
					<span class="c0"
						>Premiums can be paid using a card, wallet payment, or bank transfer. All payments must be
						successfully processed before your coverage becomes active.</span
					>
				</p>
				<h3 class="c4" id="h.74h5p7lytbqf">
					<span class="c13 c15">Late Renewals for Health Products</span>
				</h3>
				<p class="c9">
					<span class="c0">For health insurance products, penalties apply for late renewals:</span>
				</p>
				<ul class="c1 lst-kix_1btdhvwdubw9-0 start">
					<li class="c5 li-bullet-0">
						<span class="c0"
							>If you renew within 7 days after your cover expires, you or your dependants will be subject
							to a 24-hour waiting period before you can access care.<br
						/></span>
					</li>
					<li class="c5 li-bullet-0">
						<span class="c0"
							>If you renew more than 7 days after expiration, you or your dependants will face a 7-day
							waiting period before care can resume.<br
						/></span>
					</li>
				</ul>
				<p class="c9">
					<span class="c0"
						>These waiting periods are automatically applied to discourage late renewals and ensure
						continuous coverage.</span
					>
				</p>
				<h3 class="c4" id="h.5q1677o97eew"><span class="c15 c13">Refunds</span></h3>
				<p class="c9">
					<span class="c0"
						>Any approved refunds will be credited only to your MyCoverGenius wallet. Refunds will not be
						made directly to your personal bank account.</span
					>
				</p>
				<p class="c9 c14">
					<span class="c0">.<br /></span>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.kfptus8w8a64">
					<span class="c3">4. Claims Process and Subrogation</span>
				</h2>
				<p class="c9">
					<span class="c13">Notification<br /></span
					><span class="c0"
						>&nbsp;You must notify MyCoverGenius and/or the Insurer in writing within the timeline specified
						in your policy (e.g., 7&ndash;14 days) after a covered event occurs.</span
					>
				</p>
				<p class="c9">
					<span class="c13">Documentation<br /></span
					><span class="c0"
						>&nbsp;You are required to submit all necessary documents, such as proof of loss, police
						reports, invoices, or medical reports. Additional information may be requested as part of the
						claims assessment.</span
					>
				</p>
				<p class="c9">
					<span class="c13">Settlement Timeline<br /></span
					><span class="c0"
						>&nbsp;Valid claims are targeted to be settled within 60 days in line with NIIRA 2025. If there
						is a delay, interest or penalties may apply as required by law.</span
					>
				</p>
				<p class="c9">
					<span class="c13">Subrogation<br /></span
					><span class="c0"
						>&nbsp;When a third party is responsible for your loss (e.g., a vehicle accident), our
						third-party insurance provider may cover your loss first, then pursue the third party&rsquo;s
						insurer to recover the amount paid. You are required to cooperate by providing information or
						signing any documents necessary to support this process.</span
					>
				</p>
				<p class="c9">
					<span class="c13">Right of Rejection<br /></span
					><span class="c0"
						>&nbsp;Claims may be rejected wholly or partially if you fail to meet your obligations,
						including late notification, misrepresentation, or if the event is excluded from coverage.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.xbmnnhul2py6">
					<span class="c3">5. Coverage, Cancellation, and Termination</span>
				</h2>
				<p class="c9">
					<span class="c13">Term and Renewal<br /></span
					><span class="c0"
						>&nbsp;Policies are valid for the term stated in your policy schedule (e.g., one year). Renewal
						may require payment of a new premium.</span
					>
				</p>
				<p class="c9">
					<span class="c13">Cancellation by Policyholder<br /></span
					><span class="c0"
						>&nbsp;You may cancel your policy by providing written notice, subject to any minimum retention
						period and refund rules. For mid-term cancellations, refunds are calculated on a pro-rata basis
						minus applicable deductions such as administrative fees or risk run-off.</span
					>
				</p>
				<p class="c9">
					<span class="c13">Cancellation by MyCoverGenius or Insurer<br /></span
					><span class="c0"
						>&nbsp;MyCoverGenius or the Insurer may cancel or refuse renewal for legitimate business
						reasons, such as non-payment, fraud, breach of terms, or significant risk changes. Notice will
						be given (e.g., 30 days) or as required by regulation.</span
					>
				</p>
				<p class="c9">
					<span class="c13">Effect of Termination<br /></span
					><span class="c0"
						>&nbsp;Coverage stops on the termination date. Any claims or obligations incurred before
						termination remain enforceable.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.xzjetipmfodd">
					<span class="c3">6. Use of the Platform and Your Responsibilities</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>You agree to use the platform only for legitimate purposes and not to commit fraud, spam,
						tamper with services, or violate any laws.<br />
						You are responsible for maintaining the confidentiality of your login credentials.<br />
						You must not reverse engineer, decompile, or attempt to disrupt the platform.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.4phx0zawzyi0">
					<span class="c3">7. Intellectual Property</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>All content, design, logos, software, and materials on the MyCoverGenius platform are owned by
						MyCoverGenius or its licensors.<br />
						You shall not copy, reproduce, distribute, or create derivative works without prior written
						permission.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.5fqc7q8hg82t">
					<span class="c3">8. Disclaimers and Limitation of Liability</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>The platform and services are provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo;<br />
						MyCoverGenius disclaims all warranties, whether express or implied, including fitness for a
						particular purpose, accuracy, uptime, or non-infringement.</span
					>
				</p>
				<p class="c9">
					<span class="c0"
						>In no event shall MyCoverGenius or the Insurer be liable for indirect, incidental, special,
						punitive, or consequential damages.<br />
						Our aggregate liability is limited to the total amount paid under the relevant policy or as
						limited by law.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.d8olzvxa1xdq">
					<span class="c3">9. Data Protection and International Transfers</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>MyCoverGenius collects, stores, and processes personal data in line with its Privacy Policy.<br />
						If data is transferred outside Nigeria (e.g., to cloud servers, analytics providers, or service
						partners), appropriate safeguards are implemented, such as standard contractual clauses,
						encryption, and compliance with NDPR.</span
					>
				</p>
				<p class="c9">
					<span class="c0">We will notify you if any such transfer materially affects your rights.</span>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.sd6zj6i5kidu">
					<span class="c3">10. Governing Law and Dispute Resolution</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>These Terms are governed by the laws of the Federal Republic of Nigeria and applicable
						insurance regulations.<br />
						Any dispute arising from these Terms shall be resolved through arbitration in Nigeria, subject
						to regulatory oversight.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10 c16" id="h.r2utgjudv8yy"><span class="c3"></span></h2>
				<h2 class="c10 c16" id="h.q24psiapu3d9"><span class="c3"></span></h2>
				<h2 class="c10" id="h.ecn2uv4t8ebc">
					<span class="c3">11. Amendments</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>MyCoverGenius may modify these Terms at any time. Updates will be posted with a revised
						version. Continued use of the platform after such updates constitutes acceptance of the
						changes.</span
					>
				</p>
				<hr />
				<p class="c8"><span class="c0"></span></p>
				<h2 class="c10" id="h.oeq3ucbbioa5">
					<span class="c3">12. Severability</span>
				</h2>
				<p class="c9">
					<span class="c0"
						>If any provision of these Terms is found invalid or unenforceable under applicable law, that
						provision will be severed, and the remaining provisions will remain in full force and
						effect.</span
					>
				</p>
				<h2 class="c21" id="h.13nphcnn7dgi"><span class="c11">Contact Us</span></h2>
				<p class="c17">
					<span class="c2"
						>If you have any questions, complaints, or concerns about our terms and conditions or our data
						practices, contact us at:</span
					>
				</p>
				<p class="c17">
					<span class="c12">MyCoverGenius<br /></span><span class="c6">Address: </span
					><span class="c2 c19">Venia Place, Plot 8, Providence Street, Lekki Phase 1, Lagos, Nigeria</span>
				</p>
				<p class="c17">
					<span class="c2 c19">Customer Care Hotline: 08099988005</span>
				</p>
				<p class="c17">
					<span class="c6 c19">Email: hello@mycovergenius.com</span>
				</p>
				<p class="c8"><span class="c0"></span></p>
			</article>
		</ui-pad>
	</div>
</template>
<style type="text/css" scoped>
	.active {
		@apply font-semibold text-base border-main;
	}
	.lst-kix_rsxb1kdaih5c-1 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_rsxb1kdaih5c-2 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_rsxb1kdaih5c-3 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_rsxb1kdaih5c-4 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_rsxb1kdaih5c-6 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_rsxb1kdaih5c-7 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_1btdhvwdubw9-4 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_rsxb1kdaih5c-5 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_1btdhvwdubw9-2 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_1btdhvwdubw9-3 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_1btdhvwdubw9-0 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_1btdhvwdubw9-1 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_rsxb1kdaih5c-8 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_tivdy5arzsch-1 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_tivdy5arzsch-0 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_rsxb1kdaih5c-0 > li:before {
		content: '\0025cf   ';
	}
	ul.lst-kix_1btdhvwdubw9-6 {
		list-style-type: none;
	}
	ul.lst-kix_1btdhvwdubw9-7 {
		list-style-type: none;
	}
	ul.lst-kix_rsxb1kdaih5c-0 {
		list-style-type: none;
	}
	ul.lst-kix_1btdhvwdubw9-8 {
		list-style-type: none;
	}
	ul.lst-kix_rsxb1kdaih5c-1 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-0 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-2 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-1 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-4 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-3 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-6 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-5 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-8 {
		list-style-type: none;
	}
	ul.lst-kix_tivdy5arzsch-7 {
		list-style-type: none;
	}
	.lst-kix_tivdy5arzsch-2 > li:before {
		content: '\0025a0   ';
	}
	ul.lst-kix_rsxb1kdaih5c-6 {
		list-style-type: none;
	}
	.lst-kix_tivdy5arzsch-3 > li:before {
		content: '\0025cf   ';
	}
	ul.lst-kix_rsxb1kdaih5c-7 {
		list-style-type: none;
	}
	ul.lst-kix_rsxb1kdaih5c-8 {
		list-style-type: none;
	}
	ul.lst-kix_1btdhvwdubw9-0 {
		list-style-type: none;
	}
	ul.lst-kix_1btdhvwdubw9-1 {
		list-style-type: none;
	}
	ul.lst-kix_rsxb1kdaih5c-2 {
		list-style-type: none;
	}
	.lst-kix_tivdy5arzsch-5 > li:before {
		content: '\0025a0   ';
	}
	ul.lst-kix_1btdhvwdubw9-2 {
		list-style-type: none;
	}
	ul.lst-kix_rsxb1kdaih5c-3 {
		list-style-type: none;
	}
	ul.lst-kix_1btdhvwdubw9-3 {
		list-style-type: none;
	}
	ul.lst-kix_rsxb1kdaih5c-4 {
		list-style-type: none;
	}
	.lst-kix_tivdy5arzsch-4 > li:before {
		content: '\0025cb   ';
	}
	ul.lst-kix_1btdhvwdubw9-4 {
		list-style-type: none;
	}
	ul.lst-kix_rsxb1kdaih5c-5 {
		list-style-type: none;
	}
	ul.lst-kix_1btdhvwdubw9-5 {
		list-style-type: none;
	}
	.lst-kix_tivdy5arzsch-7 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_tivdy5arzsch-6 > li:before {
		content: '\0025cf   ';
	}
	li.li-bullet-0:before {
		margin-left: -18pt;
		white-space: nowrap;
		display: inline-block;
		min-width: 18pt;
	}
	.lst-kix_tivdy5arzsch-8 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_1btdhvwdubw9-5 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_1btdhvwdubw9-6 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_1btdhvwdubw9-7 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_1btdhvwdubw9-8 > li:before {
		content: '\0025a0   ';
	}
	ol {
		margin: 0;
		padding: 0;
	}
	table td,
	table th {
		padding: 0;
	}
	.c4 {
		margin-left: 36pt;
		padding-top: 14pt;
		text-indent: -18pt;
		padding-bottom: 4pt;
		line-height: 1.5;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c5 {
		margin-left: 36pt;
		padding-top: 12pt;
		padding-left: 0pt;
		padding-bottom: 12pt;
		line-height: 1.5;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c2 {
		color: #000000;
		font-weight: 400;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 12pt;

		font-style: normal;
	}
	.c0 {
		color: #000000;
		font-weight: 400;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 11pt;

		font-style: normal;
	}
	.c3 {
		color: #000000;
		font-weight: 700;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 17pt;

		font-style: normal;
	}
	.c8 {
		padding-top: 0pt;
		padding-bottom: 0pt;
		line-height: 1.5;
		orphans: 2;
		widows: 2;
		text-align: justify;
		height: 11pt;
	}
	.c11 {
		color: #000000;
		font-weight: 700;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 12pt;

		font-style: normal;
	}
	.c9 {
		padding-top: 12pt;
		padding-bottom: 12pt;
		line-height: 1.5;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c21 {
		padding-top: 18pt;
		padding-bottom: 4pt;
		line-height: 1.15;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c10 {
		padding-top: 18pt;
		padding-bottom: 4pt;
		line-height: 1.5;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c20 {
		padding-top: 24pt;
		padding-bottom: 6pt;
		line-height: 1.5;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c17 {
		padding-top: 12pt;
		padding-bottom: 12pt;
		line-height: 1.15;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c18 {
		color: #000000;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 23pt;
		font-style: normal;
	}
	.c15 {
		color: #000000;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 13pt;
		font-style: normal;
	}
	.c12 {
		font-size: 12pt;
		font-weight: 700;
	}
	.c6 {
		font-size: 12pt;
		font-weight: 400;
	}
	.c7 {
		background-color: #ffffff;
		max-width: 468pt;
		padding: 72pt 72pt 72pt 72pt;
	}
	.c13 {
		font-weight: 700;
	}
	.c1 {
		padding: 0;
		margin: 0;
	}
	.c16 {
		height: 16pt;
	}
	.c19 {
		background-color: #f8fcfb;
	}
	.c14 {
		margin-left: 36pt;
	}
	.title {
		padding-top: 0pt;
		color: #000000;
		font-size: 26pt;
		padding-bottom: 3pt;
		line-height: 1.15;
		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	.subtitle {
		padding-top: 0pt;
		color: #666666;
		font-size: 15pt;
		padding-bottom: 16pt;
		line-height: 1.15;
		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	li {
		color: #000000;
		font-size: 11pt;
	}
	p {
		margin: 0;
		color: #000000;
		font-size: 11pt;
	}
	h1 {
		padding-top: 20pt;
		color: #000000;
		font-size: 20pt;
		padding-bottom: 6pt;
		line-height: 1.15;
		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h2 {
		padding-top: 18pt;
		color: #000000;
		font-size: 16pt;
		padding-bottom: 6pt;
		line-height: 1.15;
		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h3 {
		padding-top: 16pt;
		color: #434343;
		font-size: 14pt;
		padding-bottom: 4pt;
		line-height: 1.15;
		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h4 {
		padding-top: 14pt;
		color: #666666;
		font-size: 12pt;
		padding-bottom: 4pt;
		line-height: 1.15;
		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h5 {
		padding-top: 12pt;
		color: #666666;
		font-size: 11pt;
		padding-bottom: 4pt;
		line-height: 1.15;
		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h6 {
		padding-top: 12pt;
		color: #666666;
		font-size: 11pt;
		padding-bottom: 4pt;
		line-height: 1.15;
		page-break-after: avoid;
		font-style: italic;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
</style>
